/**
 * LandingCopyEditor — 产品多语文案配置（ADM-LP-S006）
 *
 * 1:1 对照 design/Novel-admin/src/dialogs-landing.jsx LandingCopyDialog。
 */

import type { LandingCopy, LandingProductDto } from '@novel-admin/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { AdminApiError } from '~/api/errors';
import { useLandingTemplates } from '~/data/queries';
import { landingProductCopyApi } from '~/hooks/use-landing-templates';
import { langName } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { toast } from '~/ui/Toast';

const EMPTY_COPY: LandingCopy = {
  cta: '',
  hook_line1: '',
  hook_line2: '',
  topbar_tagline: '',
  topbar_cta: '',
};

function Note({
  tone = 'info',
  children,
  style,
}: {
  tone?: 'info' | 'pos';
  children: ReactNode;
  style?: CSSProperties;
}) {
  const bg = tone === 'pos' ? 'var(--pos-soft)' : 'var(--info-soft)';
  const fg = tone === 'pos' ? 'var(--pos)' : 'var(--info)';
  const Icon = tone === 'pos' ? Icons.IconCheckCircle : Icons.IconInfo;
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--radius)',
        background: bg,
        display: 'flex',
        gap: 9,
        alignItems: 'flex-start',
        ...style,
      }}
    >
      <Icon size={13} style={{ color: fg, marginTop: 2, flexShrink: 0 }} />
      <div style={{ fontSize: 'var(--text-xs)', lineHeight: 1.6, color: 'var(--fg)' }}>
        {children}
      </div>
    </div>
  );
}

export function LandingCopyEditor({
  product,
  onClose,
}: {
  product: LandingProductDto;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: templates } = useLandingTemplates();
  const langs = Object.keys(templates?.copy?.defaults ?? { en: {} });
  const [lang, setLang] = useState(langs[0] ?? 'en');
  const [draft, setDraft] = useState<LandingCopy>({ ...EMPTY_COPY });
  const [unknownKey, setUnknownKey] = useState('');

  const copyQ = useQuery({
    queryKey: ['landing-copy', product.id, lang],
    queryFn: () => landingProductCopyApi.get(product.id, lang),
    retry: false,
  });
  const source = copyQ.data?.source ?? 'default';

  useEffect(() => {
    if (!copyQ.data) return;
    setDraft({ ...EMPTY_COPY, ...copyQ.data.copy });
    setUnknownKey('');
  }, [copyQ.data]);

  const put = useMutation({
    mutationFn: () => landingProductCopyApi.put(product.id, lang, { copy: draft }),
    onSuccess: () => {
      toast(`已整份写入 ${lang} 文案配置（PUT /copy/${lang}）`, { tone: 'pos', icon: 'IconCheck' });
      void qc.invalidateQueries({ queryKey: ['landing-copy', product.id] });
    },
    onError: (e) => toast(e instanceof AdminApiError ? e.message : '保存失败', { tone: 'neg' }),
  });
  const del = useMutation({
    mutationFn: () => landingProductCopyApi.remove(product.id, lang),
    onSuccess: () => {
      toast(`已删除该语言的已存配置 · 恢复系统默认（DELETE /copy/${lang}）`, {
        tone: 'warn',
        icon: 'IconTrash',
      });
      void qc.invalidateQueries({ queryKey: ['landing-copy', product.id] });
    },
    onError: (e) => toast(e instanceof AdminApiError ? e.message : '删除失败', { tone: 'neg' }),
  });

  const defaults = templates?.copy?.defaults?.[lang] ?? {};
  const copyKeys = templates?.copy?.keys ?? [];

  return (
    <Dialog
      width={760}
      title={`页面文案配置 · ${product.name}`}
      subtitle="GET 区分 saved / default · PUT 整份写入 · DELETE 恢复系统默认"
      icon="IconType"
      onClose={onClose}
      footer={
        <div className="hstack" style={{ width: '100%', flexWrap: 'wrap' }}>
          <div
            style={{
              flex: '1 1 240px',
              minWidth: 0,
              overflowWrap: 'anywhere',
              fontSize: 'var(--text-xs)',
              color: 'var(--fg-muted)',
            }}
          >
            主键 <span className="mono">(product_id, language)</span> 两维 · 键闭集由 GET
            /landing-templates 提供，admin 不另维护键表
          </div>
          <button
            type="button"
            className="btn"
            disabled={source !== 'saved' || del.isPending}
            onClick={() => del.mutate()}
          >
            <Icons.IconTrash size={11} />
            恢复系统默认
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={put.isPending}
            onClick={() => {
              if (unknownKey.trim()) {
                toast(`VALIDATION_FAILED · 未知键「${unknownKey.trim()}」不在闭集内，整份拒绝`, {
                  tone: 'neg',
                  icon: 'IconAlert',
                });
                return;
              }
              put.mutate();
            }}
          >
            整份保存
          </button>
        </div>
      }
    >
      <div style={{ padding: '12px 18px 18px' }}>
        <div className="hstack" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {langs.map((l) => (
            <button
              key={l}
              type="button"
              className={`btn btn-sm ${lang === l ? 'btn-accent' : ''}`}
              onClick={() => setLang(l)}
            >
              <span className="mono">{l.toUpperCase()}</span>
              <span style={{ opacity: 0.75, marginLeft: 3 }}>{langName(l)}</span>
            </button>
          ))}
        </div>
        <Note style={{ marginBottom: 12 }} tone={source === 'saved' ? 'pos' : 'info'}>
          当前语言 <span className="mono">{lang}</span> 的读取结果{' '}
          <span className="mono">source={source}</span>
          {source === 'saved'
            ? ' —— 已存运营配置，任务表单以此预填。'
            : ' —— 尚无已存配置，读到的是生成器系统默认；任务表单也用它预填。'}
        </Note>

        {/*
         * 键表同样只认生成器下发的 copy.keys（接入指南：admin 不另维护键表）。
         *
         * 与任务弹窗的区别：这里是**产品级**配置，一份文案给该产品该语言的所有任务用，
         * 而顶栏是逐任务开关的 —— 故**不按 scope 过滤**，topbar 键照样要能配，
         * 只是标一下它仅在开顶栏的任务上生效。
         */}
        {copyKeys.length === 0 ? (
          <div
            className="card-flat"
            style={{ padding: '14px 12px', fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}
          >
            文案键清单不可用（novel-campaign-pages 未接入或代理失败）。admin 不写死键表，
            故此处不提供录入；生成器接上后自动出现。
          </div>
        ) : (
          <div className="card-flat" style={{ padding: '2px 12px' }}>
            {copyKeys.map((item) => (
              <FormRow
                key={item.key}
                labelWidth={130}
                gap={10}
                pad="9px 0"
                label={
                  <>
                    <div style={{ fontSize: 'var(--text-xs)', overflowWrap: 'anywhere' }}>
                      {item.name ?? item.key}
                    </div>
                    <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
                      <span
                        className="mono"
                        style={{
                          fontSize: 'var(--text-2xs)',
                          color: 'var(--fg-muted)',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {item.key}
                      </span>
                      {item.scope === 'topbar' && (
                        <span
                          className="chip mono"
                          style={{ fontSize: 'var(--text-2xs)' }}
                          title="仅在开启顶部下载条的任务上生效"
                        >
                          topbar
                        </span>
                      )}
                    </div>
                  </>
                }
              >
                <div className="vstack" style={{ gap: 4, minWidth: 0 }}>
                  <input
                    className="input"
                    aria-label={item.key}
                    value={draft[item.key]}
                    onChange={(e) => setDraft({ ...draft, [item.key]: e.target.value })}
                    placeholder={defaults[item.key] ?? ''}
                  />
                  <span
                    style={{ fontSize: 'var(--text-2xs)', color: 'var(--fg-faint)' }}
                    title={`系统默认：${defaults[item.key] ?? ''}`}
                  >
                    {draft[item.key] === (defaults[item.key] ?? '') ? '= 系统默认' : '已改写'}
                  </span>
                </div>
              </FormRow>
            ))}
            <FormRow
              label={
                <span
                  className="mono"
                  style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-faint)' }}
                >
                  其他键
                </span>
              }
              labelWidth={130}
              gap={10}
              pad="9px 0"
            >
              <input
                className="input mono"
                value={unknownKey}
                onChange={(e) => setUnknownKey(e.target.value)}
                placeholder="闭集外的键会被拒绝（VALIDATION_FAILED）"
                style={{ maxWidth: 320 }}
              />
            </FormRow>
          </div>
        )}

        <div style={{ marginTop: 12, fontSize: 'var(--text-xs)', color: 'var(--fg-muted)' }}>
          页脚隐私 / 条款 / 版权由生成器按 locale 固定提供，不在可编辑键内。
        </div>
      </div>
    </Dialog>
  );
}
